import firebase from '../../../firebase';
import { type AuditTemplate, type AuditTemplateTask } from '../../../modules/auditTemplates/types';
import {
  AuditTemplatePreset,
  AuditTemplatePresetDraft,
  buildAuditTemplateDataFromPreset,
  buildAuditTemplateTaskDataFromPreset,
  buildAuditTemplatePresetDraftFromAuditTemplate,
  normalizeAuditTemplatePresetDraft,
  normalizeAuditTemplatePresetTasks,
} from './auditTemplatePresetDraft';

const AUDIT_TEMPLATE_PRESETS_COLLECTION = 'auditTemplatePresets';

function toDate(value: any): Date | undefined {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  return new Date(value);
}

function mapPresetDoc(doc: any): AuditTemplatePreset {
  const data = doc.data() || {};

  return {
    id: doc.id,
    title: data.title || '',
    description: data.description || '',
    note: data.note || '',
    isActive: data.isActive !== false,
    guide: data.guide || null,
    tasks: normalizeAuditTemplatePresetTasks(data.tasks || []),
    createTime: toDate(data.createTime),
    updateTime: toDate(data.updateTime),
  };
}

export function subscribeAuditTemplatePresets(
  onChange: (presets: AuditTemplatePreset[]) => void,
  onError: (error: Error) => void
) {
  return firebase
    .firestore()
    .collection(AUDIT_TEMPLATE_PRESETS_COLLECTION)
    .orderBy('title')
    .onSnapshot(
      (snapshot) => {
        onChange(snapshot.docs.map(mapPresetDoc));
      },
      (error) => {
        onError(error);
      }
    );
}

export async function saveAuditTemplatePresetDraft(
  draft: AuditTemplatePresetDraft
): Promise<string> {
  const normalized = normalizeAuditTemplatePresetDraft(draft);
  const now = new Date();
  const data = {
    title: normalized.title,
    description: normalized.description,
    note: normalized.note,
    isActive: normalized.isActive,
    guide: normalized.guide || null,
    tasks: normalized.tasks,
    updateTime: now,
  };

  if (normalized.id) {
    await firebase
      .firestore()
      .collection(AUDIT_TEMPLATE_PRESETS_COLLECTION)
      .doc(normalized.id)
      .set(data, { merge: true });

    return normalized.id;
  }

  const presetRef = firebase.firestore().collection(AUDIT_TEMPLATE_PRESETS_COLLECTION).doc();
  await presetRef.set({
    ...data,
    createTime: now,
  });

  return presetRef.id;
}

export async function saveAuditTemplateAsPreset({
  orgId,
  template,
  tasks,
}: {
  orgId: string;
  template: AuditTemplate;
  tasks: AuditTemplateTask[];
}): Promise<string> {
  const draft = buildAuditTemplatePresetDraftFromAuditTemplate({
    template,
    tasks,
  });
  const presetId = await saveAuditTemplatePresetDraft(draft);

  await firebase
    .firestore()
    .collection('orgs')
    .doc(orgId)
    .collection('auditTemplates')
    .doc(template.id)
    .set(
      {
        sourcePresetId: presetId,
        sourcePresetTitle: draft.title,
      },
      { merge: true }
    );

  return presetId;
}

export async function deactivateAuditTemplatePreset(presetId: string) {
  await firebase.firestore().collection(AUDIT_TEMPLATE_PRESETS_COLLECTION).doc(presetId).set(
    {
      isActive: false,
      updateTime: new Date(),
    },
    { merge: true }
  );
}

export async function addAuditTemplatePresetToOrg({
  orgId,
  preset,
}: {
  orgId: string;
  preset: AuditTemplatePreset;
}): Promise<string> {
  const db = firebase.firestore();
  const orgDoc = await db.collection('orgs').doc(orgId).get();
  const groupId = orgDoc.data()?.groupId || null;
  const createTime = new Date();
  const tasks = normalizeAuditTemplatePresetTasks(preset.tasks);
  const templateRef = db.collection(`orgs/${orgId}/auditTemplates`).doc();
  const batch = db.batch();

  batch.set(templateRef, buildAuditTemplateDataFromPreset({ preset, orgId, groupId, createTime }));

  tasks.forEach((task, index) => {
    batch.set(
      templateRef.collection('tasks').doc(),
      buildAuditTemplateTaskDataFromPreset(task, index, createTime)
    );
  });

  await batch.commit();

  return templateRef.id;
}
